import * as React from 'react';
import { useQuery } from 'convex/react';
import { api } from '@/convex';
import type { Id } from '@/convex';
import { useClerkUser } from '@/hooks/useClerkUser';

export type GitHubConnectSource = 'onboarding' | 'settings' | 'sources';

interface UseGitHubConnectOptions {
  orgId?: Id<'orgs'>;
  projectId?: Id<'projects'> | null;
  source: GitHubConnectSource;
}

/**
 * Unified GitHub connect hook.
 *
 * The primary flow installs the GitHub App with user authorization in a
 * single redirect. `authorizeExistingInstallation()` handles the recovery
 * path where the GitHub App is already installed outside this Fragments org.
 *
 * `isConnected` reflects whether the user has an active App user-auth row
 * (per-Clerk-user). `isAppInstalled` reflects whether this Fragments org has
 * at least one active installation record.
 */
export function useGitHubConnect({
  orgId,
  projectId,
  source,
}: UseGitHubConnectOptions) {
  const { user } = useClerkUser();
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const isTestUser =
    import.meta.env.DEV &&
    import.meta.env.VITE_FRAGMENTS_TEST_MODE === '1' &&
    user?.id.startsWith('test_user_');

  // ── User-auth state (per Clerk user) ─────────────────────────
  const userAuth = useQuery(
    api.githubUserAuths.getByClerkUserRedacted,
    user?.id ? { clerkUserId: user.id } : 'skip',
  );
  const isConnected = Boolean(userAuth && userAuth.status === 'active');
  const githubLogin = userAuth?.githubLogin ?? null;

  // ── App installation state (per org) ─────────────────────────
  const installations = useQuery(
    api.githubInstallations.getByOrg,
    orgId && !isTestUser ? { orgId } : 'skip',
  );
  const isAppInstalled = Boolean(
    installations?.some((i) => i.status === 'active'),
  );
  const activeInstallation =
    installations?.find((i) => i.status === 'active') ?? null;
  const suspendedInstallation =
    installations?.find((i) => i.status === 'suspended') ?? null;
  const deletedInstallation =
    installations?.find((i) => i.status === 'deleted') ?? null;
  const isCheckingApp = !isTestUser && installations === undefined;
  const appAccountLogin = activeInstallation?.accountLogin || null;
  const appStatus = activeInstallation
    ? ('healthy' as const)
    : suspendedInstallation
      ? ('suspended' as const)
      : deletedInstallation
        ? ('deleted' as const)
        : ('missing' as const);

  const slug = import.meta.env.VITE_GITHUB_APP_SLUG as string | undefined;
  const appInstallUrl = slug
    ? `https://github.com/apps/${slug}/installations/new`
    : null;

  const startConnection = React.useCallback(
    async (intent: 'install' | 'authorize'): Promise<boolean> => {
      if (!orgId) {
        setError('Select an organization before connecting GitHub.');
        return false;
      }

      setIsConnecting(true);
      setError(null);
      let didRedirect = false;
      try {
        const response = await fetch('/api/github/install-link', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orgId, projectId, source, intent }),
        });

        const payload = (await response.json().catch(() => ({}))) as {
          error?: string;
          url?: string;
        };

        if (!response.ok) {
          throw new Error(
            payload.error ?? `Request failed (${response.status})`,
          );
        }
        if (!payload.url) {
          throw new Error('Missing GitHub URL from server response.');
        }

        didRedirect = true;
        window.location.href = payload.url;
        return true;
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to start GitHub connection.',
        );
        return false;
      } finally {
        if (!didRedirect) setIsConnecting(false);
      }
    },
    [orgId, projectId, source],
  );

  const installAndAuthorize = React.useCallback(
    () => startConnection('install'),
    [startConnection],
  );
  const authorizeExistingInstallation = React.useCallback(
    () => startConnection('authorize'),
    [startConnection],
  );

  // Backwards-compatible aliases for existing callers.
  const connect = installAndAuthorize;
  const installApp = installAndAuthorize;
  const isInstallingApp = isConnecting;

  const disconnect = React.useCallback(async () => {
    setError(null);
  }, []);

  return {
    isConnected,
    isAppInstalled,
    isCheckingApp,
    appStatus,
    activeInstallation,
    appAccountLogin,
    appInstallUrl,
    githubLogin,
    isConnecting,
    isInstallingApp,
    error,
    setError,
    connect,
    installApp,
    installAndAuthorize,
    authorizeExistingInstallation,
    disconnect,
  };
}
